import {
  esSalidaUrgente,
  NAVIERAS_DE_RESERVA,
  PREFIJO_EXPORTACIONES_MINEROS,
  type CampoDelVinculo,
  type DatosDeReserva,
  type DatosLeidos,
  type LecturaDePdf,
  type NavieraDeReserva,
  type Reserva,
  type RevisionDeVinculo,
} from '@cnc/contracts';
import {useState} from 'react';
import {Link, useSearchParams} from 'react-router';
import {ErrorDeApi} from '../api/cliente';
import {
  rutaDelPdfDeReserva,
  useConsultarNavieraDeReserva,
  useDetalleDeReserva,
  useEditarReserva,
  useEliminarReserva,
  useGuardarReserva,
  useLeerPdfDeReserva,
  useMarcarCambioRevisado,
  useReservas,
  useRevisarVinculo,
  useVincularReserva,
} from '../api/reservas';
import {AYUDA_RESERVAS as A} from '../componentes/ayuda-exportaciones';
import {ElegirMinas} from '../componentes/elegir-minas';
import {NotaDeAyuda} from '../componentes/nota-de-ayuda';
import {PistaDeSoltar, ZonaParaSoltar} from '../componentes/zona-para-soltar';
import {usePermisos} from '../componentes/sesion-contexto';
import {PestanasSeguimiento} from '../componentes/pestanas-seguimiento';
import {tr, type Texto} from '../i18n/idioma';
import {fecha, fechaHora, numero, textoOGuion} from '../utilidades/formato';

const boton =
  'rounded-md bg-verde px-3 py-1.5 text-sm font-medium text-white hover:bg-verde-oscuro disabled:opacity-50';
const botonSuave = 'rounded-md border border-linea bg-white px-3 py-1.5 text-sm hover:bg-papel disabled:opacity-50';
const campo = 'mt-1 w-full rounded-md border border-linea px-2 py-1.5 text-sm text-tinta';

// Reserva vigente que zarpa en 3 dias o menos: va resaltada (Daniel, 7/10/2026).
const urgente = (r: Reserva) => r.estado !== 'cancelada' && esSalidaUrgente(r.diasParaSalida);

const mensajeDe = (error: unknown, porDefecto: string) =>
  error instanceof ErrorDeApi ? [error.message, ...(error.detalles ?? [])].join(' ') : porDefecto;

const etiquetaNaviera = (n: NavieraDeReserva) =>
  n === 'otra'
    ? tr('Otra naviera', 'Other shipping line')
    : {maersk: 'Maersk', 'hapag-lloyd': 'Hapag-Lloyd', 'cma-cgm': 'CMA CGM'}[n];

// Reservas de las navieras: se cargan desde el PDF del booking (o a mano), se
// vinculan a una exportacion y la app vigila sus fechas.
export function PaginaReservas() {
  const permisos = usePermisos();
  const [parametros, setParametros] = useSearchParams();
  const seleccionada = Number(parametros.get('id')) || null;
  const [estado, setEstado] = useState<'activas' | 'todas'>('activas');
  const [buscar, setBuscar] = useState('');
  const reservas = useReservas(estado, buscar);
  const [formulario, setFormulario] = useState<{lectura: LecturaDePdf | null} | null>(null);
  // Codigo de exportacion escrito al guardar una reserva nueva: se revisa en el detalle.
  const [porVincular, setPorVincular] = useState<string | null>(null);

  const seleccionar = (id: number | null) => {
    const nuevos = new URLSearchParams(parametros);
    if (id === null) nuevos.delete('id');
    else nuevos.set('id', String(id));
    setParametros(nuevos);
  };

  return (
    <section className="max-w-6xl">
      <PestanasSeguimiento />
      <div className="mt-3">
        <h1 className="text-2xl font-semibold text-verde-oscuro">{tr('Reservas', 'Bookings')}</h1>
        <p className="text-sm text-apagado">
          {tr(
            'Sube el PDF del booking: la app lee los contenedores y las fechas de salida y llegada. Vincula cada reserva a su exportación y la app le asigna contenedores de 28 Big Bags del mismo exportador, en orden de recolección (Shipment #) y luego de BB #.',
            'Upload the booking PDF: the app reads the containers and the departure and arrival dates. Link each booking to its export and the app assigns it containers of 28 Big Bags from the same exporter, in order of pickup (Shipment #) and then BB #.',
          )}
        </p>
      </div>

      {permisos.editar && !formulario && (
        <CargarPdf onLeido={lectura => setFormulario({lectura})} onAMano={() => setFormulario({lectura: null})} />
      )}

      {formulario && (
        <FormularioDeReserva
          lectura={formulario.lectura}
          onCancelar={() => setFormulario(null)}
          onGuardada={(r, codigo) => {
            setFormulario(null);
            setPorVincular(r.exportacion ? null : codigo);
            seleccionar(r.id);
          }}
        />
      )}

      {seleccionada !== null && (
        <DetalleDeReserva
          key={seleccionada}
          id={seleccionada}
          codigoInicial={porVincular}
          onCerrar={() => {
            setPorVincular(null);
            seleccionar(null);
          }}
        />
      )}

      <div className="mt-6 flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-medium">{tr('Reservas registradas', 'Registered bookings')}</h2>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <input
            value={buscar}
            onChange={e => setBuscar(e.target.value)}
            placeholder={tr('Buscar booking o exportación', 'Search booking or export')}
            className="rounded-md border border-linea px-2 py-1"
          />
          <NotaDeAyuda texto={A.buscar} />
          {(
            [
              ['activas', tr('Activas', 'Active')],
              ['todas', tr('Todas', 'All')],
            ] as const
          ).map(([clave, texto]) => (
            <button
              key={clave}
              type="button"
              onClick={() => setEstado(clave)}
              className={`rounded-full border px-3 py-1 ${estado === clave ? 'border-verde bg-verde-claro text-verde-oscuro' : 'border-linea bg-white text-apagado'}`}
            >
              {texto}
            </button>
          ))}
          <NotaDeAyuda texto={A.estado} />
        </div>
      </div>
      {reservas.isError && <p className="mt-2 text-sm text-alerta">{reservas.error.message}</p>}
      {reservas.data && reservas.data.reservas.length === 0 && (
        <p className="mt-2 rounded-lg border border-linea bg-white p-4 text-sm text-apagado">
          {tr('No hay reservas registradas.', 'No bookings registered.')}
        </p>
      )}
      {reservas.data && reservas.data.reservas.length > 0 && (
        <div className="mt-2 overflow-x-auto rounded-lg border border-linea bg-white">
          <table className="w-full text-sm">
            <thead className="bg-papel text-left text-xs text-apagado">
              <tr>
                <th className="px-3 py-2 font-normal">
                  Booking
                  <NotaDeAyuda texto={A.numero} />
                </th>
                <th className="px-3 py-2 font-normal">
                  {tr('Naviera', 'Shipping line')}
                  <NotaDeAyuda texto={A.naviera} />
                </th>
                <th className="px-3 py-2 font-normal">
                  {tr('Exportación', 'Export')}
                  <NotaDeAyuda texto={A.exportacion} />
                </th>
                <th className="px-3 py-2 font-normal">
                  {tr('Contenedores completos', 'Full containers')}
                  <NotaDeAyuda texto={A.completos} />
                </th>
                <th className="px-3 py-2 font-normal">
                  {tr('Salida (ETD)', 'Departure (ETD)')}
                  <NotaDeAyuda texto={A.diasSalida} />
                </th>
                <th className="px-3 py-2 font-normal">
                  {tr('Llegada', 'Arrival')}
                  <NotaDeAyuda texto={A.llegada} />
                </th>
                <th className="px-3 py-2 font-normal">
                  {tr('Cambios', 'Changes')}
                  <NotaDeAyuda texto={A.cambios} />
                </th>
              </tr>
            </thead>
            <tbody>
              {reservas.data.reservas.map(r => (
                <tr
                  key={r.id}
                  className={`border-t border-linea/70 ${r.id === seleccionada ? 'bg-verde-claro/40' : urgente(r) ? 'bg-urgente-claro' : ''} ${r.estado === 'cancelada' ? 'text-apagado' : ''} ${urgente(r) ? 'border-l-4 border-l-urgente' : ''}`}
                >
                  <td className="px-3 py-2">
                    <button
                      type="button"
                      onClick={() => seleccionar(r.id)}
                      className="font-medium text-verde hover:underline"
                    >
                      {r.numero}
                    </button>
                    {r.estado === 'cancelada' && <span className="ml-2 text-xs">({tr('cancelada', 'cancelled')})</span>}
                  </td>
                  <td className="px-3 py-2">{r.navieraEtiqueta}</td>
                  <td className="px-3 py-2">
                    {r.exportacion ? (
                      <Link
                        to={`/seguimiento/${encodeURIComponent(r.exportacion.codigo)}`}
                        className="text-verde hover:underline"
                      >
                        {r.exportacion.codigo}
                      </Link>
                    ) : (
                      <span className="text-alerta">{tr('sin vincular', 'not linked')}</span>
                    )}
                  </td>
                  <td className="px-3 py-2 tabular-nums">
                    <span className={r.contenedoresCompletos < r.contenedores ? 'text-alerta' : 'text-verde-oscuro'}>
                      {numero(r.contenedoresCompletos)} / {numero(r.contenedores)}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    {fecha(r.etd)}{' '}
                    {urgente(r) ? (
                      <span className="rounded-full bg-urgente px-2 py-0.5 text-xs font-semibold text-white">
                        {r.diasParaSalida === 0
                          ? tr('zarpa hoy', 'departs today')
                          : tr(`zarpa en ${r.diasParaSalida} días`, `departs in ${r.diasParaSalida} days`)}
                      </span>
                    ) : (
                      <span className="text-xs text-apagado">
                        {r.diasParaSalida >= 0 ? tr(`(${r.diasParaSalida} días)`, `(${r.diasParaSalida} days)`) : ''}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2">{fecha(r.etaFinal ?? r.etaPuerto)}</td>
                  <td className="px-3 py-2">
                    {r.cambiosSinRevisar > 0 ? (
                      <span className="rounded-full bg-dorado-claro px-2 py-0.5 text-xs">
                        {tr(`${r.cambiosSinRevisar} por revisar`, `${r.cambiosSinRevisar} to review`)}
                      </span>
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function CargarPdf({onLeido, onAMano}: {onLeido: (l: LecturaDePdf) => void; onAMano: () => void}) {
  const leer = useLeerPdfDeReserva();
  const [error, setError] = useState<string | null>(null);
  return (
    <ZonaParaSoltar className="mt-4 rounded-lg border border-linea bg-white p-4" deshabilitado={leer.isPending}>
      <div className="flex flex-wrap items-center gap-3">
        <label className={`${boton} cursor-pointer`}>
          {leer.isPending
            ? tr('Leyendo el PDF…', 'Reading the PDF…')
            : tr('↑ Subir PDF del booking', '↑ Upload booking PDF')}
          <input
            type="file"
            accept="application/pdf,.pdf"
            className="sr-only"
            aria-label={tr('PDF del booking', 'Booking PDF')}
            disabled={leer.isPending}
            onChange={e => {
              const archivo = e.target.files?.[0];
              e.target.value = '';
              if (!archivo) return;
              setError(null);
              leer
                .mutateAsync(archivo)
                .then(onLeido, (err: unknown) =>
                  setError(mensajeDe(err, tr('No se pudo leer el PDF.', 'Could not read the PDF.'))),
                );
            }}
          />
        </label>
        <button type="button" className={botonSuave} onClick={onAMano} disabled={leer.isPending}>
          {tr('Registrar a mano', 'Enter manually')}
        </button>
        <span className="text-xs text-apagado">
          {tr(
            'Maersk, Hapag-Lloyd y CMA CGM. Si el PDF es una imagen, la lectura tarda unos segundos más.',
            'Maersk, Hapag-Lloyd and CMA CGM. If the PDF is an image, reading takes a few more seconds.',
          )}
        </span>
        {!leer.isPending && <PistaDeSoltar />}
      </div>
      {error && <p className="mt-2 text-sm text-alerta">{error}</p>}
    </ZonaParaSoltar>
  );
}

type Valores = Record<
  | 'numero'
  | 'contenedores'
  | 'tipoContenedor'
  | 'buque'
  | 'viaje'
  | 'puertoCarga'
  | 'puertoDescarga'
  | 'destinoFinal'
  | 'fechaReserva'
  | 'etd'
  | 'etaPuerto'
  | 'etaFinal'
  | 'cierreDocumentos'
  | 'cierreVgm'
  | 'cierrePuerto'
  | 'diasLibres'
  | 'exportacion',
  string
> & {naviera: NavieraDeReserva};

const valoresDe = (d: Partial<DatosLeidos> | Partial<DatosDeReserva> | null, exportacion = ''): Valores => ({
  naviera: d?.naviera ?? 'maersk',
  numero: d?.numero ?? '',
  contenedores: d?.contenedores ? String(d.contenedores) : '',
  tipoContenedor: d?.tipoContenedor ?? '',
  buque: d?.buque ?? '',
  viaje: d?.viaje ?? '',
  puertoCarga: d?.puertoCarga ?? '',
  puertoDescarga: d?.puertoDescarga ?? '',
  destinoFinal: d?.destinoFinal ?? '',
  fechaReserva: d?.fechaReserva ?? '',
  etd: d?.etd ?? '',
  etaPuerto: d?.etaPuerto ?? '',
  etaFinal: d?.etaFinal ?? '',
  // El campo de fecha y hora usa "AAAA-MM-DDTHH:MM".
  cierreDocumentos: aCampoDeHora(d?.cierreDocumentos),
  cierreVgm: aCampoDeHora(d?.cierreVgm),
  cierrePuerto: aCampoDeHora(d?.cierrePuerto),
  diasLibres: d?.diasLibres == null ? '' : String(d.diasLibres),
  exportacion,
});

// Cierre como lo imprime la naviera: fecha y hora de Colombia.
const conHora = (v: string | null) => (v ? `${fecha(v.slice(0, 10))}${v.length > 10 ? ` ${v.slice(11)}` : ''}` : '—');

function aCampoDeHora(valor: string | null | undefined): string {
  if (!valor) return '';
  return valor.length === 10 ? `${valor}T00:00` : valor.replace(' ', 'T');
}
const deCampoDeHora = (v: string) => (v.trim() ? v.trim().replace('T', ' ').slice(0, 16) : null);

const nulo = (v: string) => (v.trim() ? v.trim() : null);

const datosDe = (v: Valores): Omit<DatosDeReserva, 'tramos'> => ({
  naviera: v.naviera,
  numero: v.numero.trim(),
  contenedores: Math.round(Number(v.contenedores)),
  tipoContenedor: nulo(v.tipoContenedor),
  buque: nulo(v.buque),
  viaje: nulo(v.viaje),
  puertoCarga: nulo(v.puertoCarga),
  puertoDescarga: nulo(v.puertoDescarga),
  destinoFinal: nulo(v.destinoFinal),
  fechaReserva: nulo(v.fechaReserva),
  etd: v.etd,
  etaPuerto: nulo(v.etaPuerto),
  etaFinal: nulo(v.etaFinal),
  cierreDocumentos: deCampoDeHora(v.cierreDocumentos),
  cierreVgm: deCampoDeHora(v.cierreVgm),
  cierrePuerto: deCampoDeHora(v.cierrePuerto),
  diasLibres: v.diasLibres.trim() ? Math.max(0, Math.round(Number(v.diasLibres))) : null,
});

function CamposDeReserva({valores, onCambiar}: {valores: Valores; onCambiar: (v: Valores) => void}) {
  const texto = (clave: Exclude<keyof Valores, 'naviera'>, etiqueta: string, tipo = 'text', requerido = false) => (
    <label className="block text-xs text-apagado">
      {etiqueta}
      {requerido && ' *'}
      <NotaDeAyuda texto={A[clave as keyof typeof A]} dentroDeEtiqueta />
      <input
        type={tipo}
        min={tipo === 'number' ? (clave === 'diasLibres' ? 0 : 1) : undefined}
        required={requerido}
        value={valores[clave]}
        onChange={e => onCambiar({...valores, [clave]: e.target.value})}
        className={campo}
        aria-label={etiqueta}
      />
    </label>
  );
  return (
    <div className="grid gap-3 sm:grid-cols-4">
      <label className="block text-xs text-apagado">
        {tr('Naviera', 'Shipping line')} *
        <NotaDeAyuda texto={A.naviera} dentroDeEtiqueta />
        <select
          value={valores.naviera}
          onChange={e => onCambiar({...valores, naviera: e.target.value as NavieraDeReserva})}
          className={campo}
          aria-label={tr('Naviera', 'Shipping line')}
        >
          {NAVIERAS_DE_RESERVA.map(n => (
            <option key={n} value={n}>
              {etiquetaNaviera(n)}
            </option>
          ))}
        </select>
      </label>
      {texto('numero', tr('Número de booking', 'Booking number'), 'text', true)}
      {texto('contenedores', tr('Contenedores', 'Containers'), 'number', true)}
      {texto('tipoContenedor', tr('Tipo de contenedor', 'Container type'))}
      {texto('etd', tr('Salida (ETD)', 'Departure (ETD)'), 'date', true)}
      {texto('etaPuerto', tr('Llegada al puerto (ETA)', 'Arrival at port (ETA)'), 'date')}
      {texto('etaFinal', tr('Llegada al destino final', 'Arrival at final destination'), 'date')}
      {texto('fechaReserva', tr('Fecha de la reserva', 'Booking date'), 'date')}
      {texto('buque', tr('Buque', 'Vessel'))}
      {texto('viaje', tr('Viaje', 'Voyage'))}
      {texto('puertoCarga', tr('Puerto de carga', 'Port of loading'))}
      {texto('puertoDescarga', tr('Puerto de descarga', 'Port of discharge'))}
      {texto('destinoFinal', tr('Destino final', 'Final destination'))}
      {texto('cierreDocumentos', tr('Cierre de documentos (SI)', 'SI cut-off'), 'datetime-local')}
      {texto('cierreVgm', tr('Cierre del VGM', 'VGM cut-off'), 'datetime-local')}
      {texto('cierrePuerto', tr('Cierre de puerto', 'Port cut-off'), 'datetime-local')}
      {texto('diasLibres', tr('Días libres en destino', 'Free days at destination'), 'number')}
    </div>
  );
}

function FormularioDeReserva({
  lectura,
  onCancelar,
  onGuardada,
}: {
  lectura: LecturaDePdf | null;
  onCancelar: () => void;
  onGuardada: (r: Reserva, codigoDeExportacion: string | null) => void;
}) {
  const guardar = useGuardarReserva();
  const [valores, setValores] = useState<Valores>(() => valoresDe(lectura?.datos ?? null));
  const [error, setError] = useState<string | null>(null);

  return (
    <form
      className="mt-4 space-y-3 rounded-lg border border-linea bg-papel p-4"
      aria-label={tr('Datos de la reserva', 'Booking details')}
      onSubmit={e => {
        e.preventDefault();
        setError(null);
        guardar
          .mutateAsync({
            ...datosDe(valores),
            tramos: lectura?.datos.tramos ?? [],
            archivoId: lectura?.archivoId ?? null,
            // El vinculo se revisa despues, en el detalle (decision 0027).
            exportacion: null,
          })
          .then(
            r => onGuardada(r, nulo(valores.exportacion)),
            (err: unknown) => setError(mensajeDe(err, tr('No se pudo guardar.', 'Could not save.'))),
          );
      }}
    >
      <div className="text-sm font-medium">
        {lectura
          ? tr(`Revisa lo que se leyó de ${lectura.nombreArchivo}`, `Check what was read from ${lectura.nombreArchivo}`)
          : tr('Nueva reserva', 'New booking')}
      </div>
      {lectura?.metodo === 'ocr' && (
        <p className="text-xs text-apagado">
          {tr(
            'El PDF es una imagen: se leyó con reconocimiento de texto. Revisa bien los números y las fechas.',
            'The PDF is an image: it was read with text recognition. Check numbers and dates carefully.',
          )}
        </p>
      )}
      {lectura && lectura.avisos.length > 0 && (
        <ul className="space-y-1 rounded-md border border-dorado/50 bg-dorado-claro px-3 py-2 text-xs">
          {lectura.avisos.map(a => (
            <li key={a}>⚠ {a}</li>
          ))}
        </ul>
      )}
      {lectura?.existente && (
        <div className="rounded-md border border-verde/40 bg-white px-3 py-2 text-xs">
          {lectura.existente.diferencias.length === 0 ? (
            tr(
              'Esta reserva ya está registrada y el PDF no trae cambios en fechas, contenedores ni buque.',
              'This booking is already registered and the PDF brings no changes to dates, containers or vessel.',
            )
          ) : (
            <>
              <div className="font-medium">
                {tr(
                  'Esta reserva ya está registrada. Al guardar cambia:',
                  'This booking is already registered. Saving will change:',
                )}
              </div>
              <ul className="mt-1">
                {lectura.existente.diferencias.map(d => (
                  <li key={d.campo}>
                    {d.etiqueta}: <span className="line-through">{valorLegible(d.antes)}</span> →{' '}
                    <span className="font-medium">{valorLegible(d.despues)}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
      <CamposDeReserva valores={valores} onCambiar={setValores} />
      {lectura && lectura.datos.tramos && lectura.datos.tramos.length > 0 && <Tramos tramos={lectura.datos.tramos} />}
      {!lectura?.existente && (
        <label className="block max-w-xs text-xs text-apagado">
          {tr('Vincular a la exportación (opcional)', 'Link to export (optional)')}
          <NotaDeAyuda texto={A.vincular} dentroDeEtiqueta />
          <input
            value={valores.exportacion}
            onChange={e => setValores({...valores, exportacion: e.target.value})}
            placeholder={tr('Código de exportación', 'Export code')}
            className={campo}
            aria-label={tr('Vincular a la exportación', 'Link to export')}
          />
        </label>
      )}
      {error && <p className="text-sm text-alerta">{error}</p>}
      <div className="flex gap-2">
        <button type="submit" className={boton} disabled={guardar.isPending}>
          {guardar.isPending ? tr('Guardando…', 'Saving…') : tr('Guardar reserva', 'Save booking')}
        </button>
        <button type="button" className={botonSuave} onClick={onCancelar}>
          {tr('Cancelar', 'Cancel')}
        </button>
      </div>
    </form>
  );
}

const valorLegible = (v: string | null) => (v && /^\d{4}-\d{2}-\d{2}$/.test(v) ? fecha(v) : (v ?? '—'));

function Tramos({tramos}: {tramos: NonNullable<DatosLeidos['tramos']>}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead className="text-left text-apagado">
          <tr>
            <th className="py-1 font-normal">{tr('Desde', 'From')}</th>
            <th className="py-1 font-normal">{tr('Hacia', 'To')}</th>
            <th className="py-1 font-normal">
              {tr('Medio', 'Mode')}
              <NotaDeAyuda texto={A.tramoMedio} />
            </th>
            <th className="py-1 font-normal">{tr('Buque / viaje', 'Vessel / voyage')}</th>
            <th className="py-1 font-normal">{tr('Salida', 'Departure')}</th>
            <th className="py-1 font-normal">{tr('Llegada', 'Arrival')}</th>
          </tr>
        </thead>
        <tbody>
          {tramos.map((t, i) => (
            <tr key={i} className="border-t border-linea/70">
              <td className="py-1">{t.desde}</td>
              <td className="py-1">{t.hacia}</td>
              <td className="py-1">{textoOGuion(t.medio)}</td>
              <td className="py-1">{[t.buque, t.viaje].filter(Boolean).join(' / ') || '—'}</td>
              <td className="py-1">{fecha(t.salida)}</td>
              <td className="py-1">{fecha(t.llegada)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

type Mensaje = {texto: string; error: boolean};

// Vincular a una exportacion (decision 0027). Primero se revisa:
// - si no existe, se crea con el codigo que se escribio y los datos de la reserva;
// - si existe y ya tiene booking u otros datos distintos, se muestran al lado
//   de los de la reserva para elegir o escribir lo que queda;
// - si la reserva pide mas Big Bags de los que tiene la exportacion, no se vincula.
function VincularExportacion({
  id,
  codigoInicial,
  onMensaje,
}: {
  id: number;
  codigoInicial: string | null;
  onMensaje: (m: Mensaje | null) => void;
}) {
  const revisar = useRevisarVinculo();
  const vincular = useVincularReserva();
  const [codigo, setCodigo] = useState(codigoInicial ?? '');
  const [revision, setRevision] = useState<RevisionDeVinculo | null>(null);
  const [valores, setValores] = useState<Partial<Record<CampoDelVinculo, string>>>({});
  const [aviso, setAviso] = useState<string | null>(null);
  // Minas de las que se asignan Big Bags (ninguna = todas las del exportador).
  const [minas, setMinas] = useState<number[]>([]);
  const ocupado = revisar.isPending || vincular.isPending;
  const elegirMinas = (rv: RevisionDeVinculo) =>
    rv.minas.length > 1 && (
      <div className="mt-2 rounded-md bg-white/70 px-2 py-1.5">
        <ElegirMinas minas={rv.minas} elegidas={minas} onCambiar={setMinas} deshabilitado={ocupado} />
        <p className="mt-1 text-xs text-apagado">
          {tr(
            `Solo salen las minas de ${rv.exportador ?? 'el exportador'} con Big Bags disponibles.`,
            `Only ${rv.exportador ?? 'the exporter'}'s mines with available Big Bags are listed.`,
          )}
          <NotaDeAyuda texto={A.minas} />
        </p>
      </div>
    );

  const fallo = (error: unknown) =>
    onMensaje({texto: mensajeDe(error, tr('No se pudo vincular.', 'Could not link.')), error: true});

  const aplicar = async (rv: RevisionDeVinculo, conValores: boolean) => {
    try {
      await vincular.mutateAsync({
        id,
        exportacion: rv.codigo,
        valores: conValores
          ? Object.fromEntries(rv.campos.map(c => [c.campo, (valores[c.campo] ?? '').trim() || null]))
          : undefined,
        minas: rv.minas.length > 1 ? minas : undefined,
      });
      setRevision(null);
      setCodigo('');
      onMensaje({
        texto: rv.existe
          ? rv.bigBags > 0
            ? tr(
                'Reserva vinculada. Los Big Bags de la exportación quedan en los contenedores de la reserva.',
                'Booking linked. The export’s Big Bags go into the booking’s containers.',
              )
            : tr(
                'Reserva vinculada. La app asigna los contenedores que ya se puedan completar.',
                'Booking linked. The app assigns the containers that can already be filled.',
              )
          : tr(
              `Exportación ${rv.codigo} creada y vinculada a la reserva.`,
              `Export ${rv.codigo} created and linked to the booking.`,
            ),
        error: false,
      });
    } catch (error) {
      fallo(error);
    }
  };

  const empezar = async () => {
    onMensaje(null);
    try {
      const rv = await revisar.mutateAsync({id, exportacion: codigo.trim()});
      if (rv.bloqueo) {
        setRevision(null);
        onMensaje({texto: rv.bloqueo, error: true});
        return;
      }
      setAviso(rv.aviso);
      // Con una sola mina (o ninguna) no hay nada que elegir; si la exportacion ya
      // tiene Big Bags, se usan los suyos (decision 0086) y tampoco.
      if (rv.existe && !rv.revisar && (rv.minas.length < 2 || rv.bigBags > 0)) return aplicar(rv, false);
      setMinas([]);
      // Lo que ya tiene la exportacion se queda; de la reserva, lo que falta (decision 0086).
      setValores(Object.fromEntries(rv.campos.map(c => [c.campo, c.exportacion ?? c.reserva ?? ''])));
      setRevision(rv);
    } catch (error) {
      fallo(error);
    }
  };

  return (
    <div className="space-y-3">
      <form
        className="flex flex-wrap items-center gap-2"
        onSubmit={e => {
          e.preventDefault();
          void empezar();
        }}
      >
        <span className="text-alerta">{tr('Sin exportación vinculada.', 'No export linked.')}</span>
        <input
          value={codigo}
          onChange={e => {
            setCodigo(e.target.value);
            setRevision(null);
            setAviso(null);
          }}
          placeholder={tr('Código de exportación', 'Export code')}
          aria-label={tr('Código de exportación', 'Export code')}
          className="rounded-md border border-linea px-2 py-1 text-sm"
          required
        />
        <NotaDeAyuda texto={A.vincular} />
        <button type="submit" className={boton} disabled={ocupado}>
          {tr('Vincular', 'Link')}
        </button>
        {codigoInicial && !revision && (
          <span className="text-xs text-apagado">
            {tr('Revisa el código y pulsa Vincular.', 'Check the code and press Link.')}
          </span>
        )}
      </form>
      {aviso && <p className="rounded-md border border-dorado/50 bg-dorado-claro px-3 py-1.5 text-sm">{aviso}</p>}

      {revision && !revision.existe && (
        <div
          role="region"
          aria-label={tr('Crear exportación', 'Create export')}
          className="rounded-md border border-dorado/50 bg-dorado-claro px-3 py-2"
        >
          <p>
            {revision.codigo.toUpperCase().startsWith(PREFIJO_EXPORTACIONES_MINEROS)
              ? tr(
                  `La exportación ${revision.codigo} no existe. Se creará como exportación de Mineros con ese código y con el booking, las fechas, el buque y la naviera de esta reserva; se le asignan Big Bags de Mineros que ya estén en la bodega de Cartagena.`,
                  `Export ${revision.codigo} does not exist. It will be created as a Mineros export with that code and with this booking's number, dates, vessel and shipping line; Mineros Big Bags already in the Cartagena warehouse are assigned to it.`,
                )
              : tr(
                  `La exportación ${revision.codigo} no existe. Se creará como exportación propia con ese código y con el booking, las fechas, el buque y la naviera de esta reserva.`,
                  `Export ${revision.codigo} does not exist. It will be created as an own export with that code and with this booking's number, dates, vessel and shipping line.`,
                )}
          </p>
          {elegirMinas(revision)}
          <div className="mt-2 flex gap-2">
            <button type="button" className={boton} disabled={ocupado} onClick={() => void aplicar(revision, false)}>
              {tr('Crear y vincular', 'Create and link')}
            </button>
            <button type="button" className={botonSuave} onClick={() => setRevision(null)}>
              {tr('Cancelar', 'Cancel')}
            </button>
          </div>
        </div>
      )}

      {revision?.existe && !revision.revisar && (
        <div
          role="region"
          aria-label={tr('Elegir minas antes de vincular', 'Choose mines before linking')}
          className="rounded-md border border-dorado/50 bg-white px-3 py-2"
        >
          <p className="font-medium">
            {tr(
              `Vincular la reserva a ${revision.codigo}: elige de qué minas se asignan los Big Bags.`,
              `Link the booking to ${revision.codigo}: choose which mines the Big Bags come from.`,
            )}
          </p>
          {elegirMinas(revision)}
          <div className="mt-2 flex gap-2">
            <button type="button" className={boton} disabled={ocupado} onClick={() => void aplicar(revision, false)}>
              {tr('Vincular', 'Link')}
            </button>
            <button type="button" className={botonSuave} onClick={() => setRevision(null)}>
              {tr('Cancelar', 'Cancel')}
            </button>
          </div>
        </div>
      )}

      {revision?.existe && revision.revisar && (
        <div
          role="region"
          aria-label={tr('Revisar datos antes de vincular', 'Review data before linking')}
          className="rounded-md border border-dorado/50 bg-white px-3 py-2"
        >
          <p className="font-medium">
            {tr(
              `La exportación ${revision.codigo} ya tiene datos. Revisa qué queda en el seguimiento:`,
              `Export ${revision.codigo} already has data. Choose what stays in tracking:`,
            )}
          </p>
          <table className="mt-2 w-full text-xs">
            <thead className="text-left text-apagado">
              <tr>
                <th className="py-1 font-normal">
                  {tr('Dato', 'Field')}
                  <NotaDeAyuda texto={A.revisionDato} />
                </th>
                <th className="py-1 font-normal">
                  {tr('En la exportación', 'In the export')}
                  <NotaDeAyuda texto={A.revisionExportacion} />
                </th>
                <th className="py-1 font-normal">
                  {tr('En la reserva', 'In the booking')}
                  <NotaDeAyuda texto={A.revisionReserva} />
                </th>
                <th className="py-1 font-normal">
                  {tr('Queda', 'Keep')}
                  <NotaDeAyuda texto={A.revisionQueda} />
                </th>
              </tr>
            </thead>
            <tbody>
              {revision.campos.map(c => {
                const distinto = c.exportacion && c.reserva && c.exportacion !== c.reserva;
                return (
                  <tr key={c.campo} className={`border-t border-linea/70 ${distinto ? 'bg-dorado-claro/60' : ''}`}>
                    <td className="py-1 font-medium">{c.etiqueta}</td>
                    <td className="py-1">
                      {c.exportacion ? (
                        <button
                          type="button"
                          className="text-verde hover:underline"
                          title={tr('Usar el de la exportación', 'Use the export value')}
                          onClick={() => setValores(v => ({...v, [c.campo]: c.exportacion!}))}
                        >
                          {valorLegible(c.exportacion)}
                        </button>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="py-1">
                      {c.reserva ? (
                        <button
                          type="button"
                          className="text-verde hover:underline"
                          title={tr('Usar el de la reserva', 'Use the booking value')}
                          onClick={() => setValores(v => ({...v, [c.campo]: c.reserva!}))}
                        >
                          {valorLegible(c.reserva)}
                        </button>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="py-1">
                      <input
                        value={valores[c.campo] ?? ''}
                        onChange={e => setValores(v => ({...v, [c.campo]: e.target.value}))}
                        aria-label={tr(`Queda: ${c.etiqueta}`, `Keep: ${c.etiqueta}`)}
                        className="w-full rounded-md border border-linea px-2 py-0.5"
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p className="mt-1 text-xs text-apagado">
            {tr(
              'Clic en un valor lo copia a "Queda". Si dejas "Queda" vacío, la exportación conserva lo que tiene.',
              'Click a value to copy it to "Keep". If "Keep" is empty, the export keeps what it has.',
            )}
          </p>
          {elegirMinas(revision)}
          <div className="mt-2 flex gap-2">
            <button type="button" className={boton} disabled={ocupado} onClick={() => void aplicar(revision, true)}>
              {tr('Guardar y vincular', 'Save and link')}
            </button>
            <button type="button" className={botonSuave} onClick={() => setRevision(null)}>
              {tr('Cancelar', 'Cancel')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function DetalleDeReserva({
  id,
  codigoInicial = null,
  onCerrar,
}: {
  id: number;
  codigoInicial?: string | null;
  onCerrar: () => void;
}) {
  const permisos = usePermisos();
  const detalle = useDetalleDeReserva(id);
  const editar = useEditarReserva();
  const vincular = useVincularReserva();
  const eliminarReserva = useEliminarReserva();
  const consultar = useConsultarNavieraDeReserva();
  const marcar = useMarcarCambioRevisado();
  const [editando, setEditando] = useState<Valores | null>(null);
  const [mensaje, setMensaje] = useState<Mensaje | null>(null);

  const ejecutar = async (accion: () => Promise<string | null>) => {
    setMensaje(null);
    try {
      const texto = await accion();
      if (texto) setMensaje({texto, error: false});
      return true;
    } catch (error) {
      setMensaje({
        texto: mensajeDe(error, tr('No se pudo hacer el cambio.', 'Could not make the change.')),
        error: true,
      });
      return false;
    }
  };

  if (detalle.isError)
    return (
      <p className="mt-4 rounded-lg border border-linea bg-white p-4 text-sm text-alerta">{detalle.error.message}</p>
    );
  if (!detalle.data) return null;
  const {reserva: r, cambios} = detalle.data;

  return (
    <div
      className="mt-4 rounded-lg border border-verde/40 bg-white p-5"
      aria-label={tr('Detalle de la reserva', 'Booking details')}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-verde-oscuro">
            {r.navieraEtiqueta} {r.numero}
            {r.estado === 'cancelada' && (
              <span className="ml-2 text-sm font-normal text-alerta">({tr('cancelada', 'cancelled')})</span>
            )}
          </h2>
          <div className="text-xs text-apagado">
            {r.origen === 'pdf'
              ? tr('Cargada desde el PDF', 'Loaded from the PDF')
              : tr('Registrada a mano', 'Entered manually')}
            {' · '}
            {tr('actualizada', 'updated')} {fechaHora(r.actualizadaEl)}
            {r.ultimaConsultaNaviera &&
              ` · ${tr('última consulta a la naviera', 'last shipping line check')} ${fechaHora(r.ultimaConsultaNaviera)}`}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {r.archivoId && (
            <a href={rutaDelPdfDeReserva(r.id)} className={botonSuave} target="_blank" rel="noreferrer">
              {tr('Ver PDF', 'View PDF')}
            </a>
          )}
          {permisos.editar && r.naviera !== 'otra' && (
            <button
              type="button"
              className={botonSuave}
              disabled={consultar.isPending}
              onClick={() => void ejecutar(async () => (await consultar.mutateAsync(r.id)).mensaje)}
            >
              {consultar.isPending
                ? tr('Consultando…', 'Checking…')
                : tr('↻ Consultar la naviera', '↻ Check shipping line')}
            </button>
          )}
          {permisos.editar && !editando && (
            <button type="button" className={botonSuave} onClick={() => setEditando(valoresDe(r))}>
              {tr('Editar', 'Edit')}
            </button>
          )}
          {permisos.editar && (
            <button
              type="button"
              className={botonSuave}
              disabled={editar.isPending}
              onClick={() => {
                const cancelar = r.estado === 'activa';
                if (
                  cancelar &&
                  !window.confirm(
                    tr(
                      '¿Marcar la reserva como cancelada? Deja de vigilarse y de generar alertas; los contenedores ya asignados no se tocan.',
                      'Mark the booking as cancelled? It stops being watched and raising alerts; containers already assigned are not touched.',
                    ),
                  )
                )
                  return;
                void ejecutar(async () => {
                  await editar.mutateAsync({id: r.id, cambios: {estado: cancelar ? 'cancelada' : 'activa'}});
                  return null;
                });
              }}
            >
              {r.estado === 'activa' ? tr('Cancelar reserva', 'Cancel booking') : tr('Reactivar', 'Reactivate')}
            </button>
          )}
          {permisos.administrar && (
            <button
              type="button"
              className="rounded-md border border-alerta/50 bg-white px-3 py-1.5 text-sm text-alerta hover:bg-alerta/10 disabled:opacity-50"
              disabled={eliminarReserva.isPending}
              onClick={() => {
                if (
                  !window.confirm(
                    tr(
                      `¿Eliminar la reserva ${r.numero}? Se borra con su historial de cambios de fecha; se puede deshacer desde la Bitácora.${r.exportacion ? ` La exportación ${r.exportacion.codigo} conserva sus contenedores y Big Bags.` : ''}`,
                      `Delete booking ${r.numero}? It is deleted with its date-change history; it can be undone from the Audit log.${r.exportacion ? ` Export ${r.exportacion.codigo} keeps its containers and Big Bags.` : ''}`,
                    ),
                  )
                )
                  return;
                void ejecutar(async () => {
                  await eliminarReserva.mutateAsync(r.id);
                  onCerrar();
                  return null;
                });
              }}
            >
              {tr('🗑 Eliminar reserva', '🗑 Delete booking')}
            </button>
          )}
          <button type="button" className="text-xs text-apagado hover:text-tinta" onClick={onCerrar}>
            ✕ {tr('Cerrar', 'Close')}
          </button>
        </div>
      </div>

      {mensaje && (
        <p className={`mt-3 text-sm ${mensaje.error ? 'text-alerta' : 'text-verde-oscuro'}`} role="status">
          {mensaje.texto}
        </p>
      )}

      {editando ? (
        <form
          className="mt-4 space-y-3 rounded-lg border border-linea bg-papel p-4"
          onSubmit={e => {
            e.preventDefault();
            void ejecutar(async () => {
              await editar.mutateAsync({id: r.id, cambios: datosDe(editando)});
              setEditando(null);
              return null;
            });
          }}
        >
          <CamposDeReserva valores={editando} onCambiar={setEditando} />
          <div className="flex gap-2">
            <button type="submit" className={boton} disabled={editar.isPending}>
              {tr('Guardar cambios', 'Save changes')}
            </button>
            <button type="button" className={botonSuave} onClick={() => setEditando(null)}>
              {tr('Cancelar', 'Cancel')}
            </button>
          </div>
        </form>
      ) : (
        <dl className="mt-4 grid gap-x-6 gap-y-2 text-sm sm:grid-cols-3 lg:grid-cols-4">
          <Dato
            titulo={tr('Contenedores', 'Containers')}
            valor={`${numero(r.contenedores)}${r.tipoContenedor ? ` × ${r.tipoContenedor}` : ''}`}
            ayuda={A.contenedores}
          />
          <Dato titulo={tr('Salida (ETD)', 'Departure (ETD)')} valor={fecha(r.etd)} ayuda={A.etd} />
          <Dato
            titulo={tr('Llegada al puerto (ETA)', 'Arrival at port (ETA)')}
            valor={fecha(r.etaPuerto)}
            ayuda={A.etaPuerto}
          />
          <Dato
            titulo={tr('Llegada al destino final', 'Arrival at final destination')}
            valor={fecha(r.etaFinal)}
            ayuda={A.etaFinal}
          />
          <Dato
            titulo={tr('Buque / viaje', 'Vessel / voyage')}
            valor={[r.buque, r.viaje].filter(Boolean).join(' / ') || '—'}
            ayuda={A.buqueViaje}
          />
          <Dato
            titulo={tr('Puerto de carga', 'Port of loading')}
            valor={textoOGuion(r.puertoCarga)}
            ayuda={A.puertoCarga}
          />
          <Dato
            titulo={tr('Puerto de descarga', 'Port of discharge')}
            valor={textoOGuion(r.puertoDescarga)}
            ayuda={A.puertoDescarga}
          />
          <Dato
            titulo={tr('Destino final', 'Final destination')}
            valor={textoOGuion(r.destinoFinal)}
            ayuda={A.destinoFinal}
          />
          <Dato
            titulo={tr('Fecha de la reserva', 'Booking date')}
            valor={fecha(r.fechaReserva)}
            ayuda={A.fechaReserva}
          />
          <Dato
            titulo={tr('Cierre de documentos (SI)', 'SI cut-off')}
            valor={conHora(r.cierreDocumentos)}
            ayuda={A.cierreDocumentos}
          />
          <Dato titulo={tr('Cierre del VGM', 'VGM cut-off')} valor={conHora(r.cierreVgm)} ayuda={A.cierreVgm} />
          <Dato
            titulo={tr('Cierre de puerto', 'Port cut-off')}
            valor={conHora(r.cierrePuerto)}
            ayuda={A.cierrePuerto}
          />
          <Dato
            titulo={tr('Días libres en destino', 'Free days at destination')}
            valor={r.diasLibres == null ? '—' : String(r.diasLibres)}
            ayuda={A.diasLibres}
          />
        </dl>
      )}

      {r.tramos.length > 0 && (
        <div className="mt-4">
          <h3 className="text-sm font-medium">
            {tr('Tramos del viaje', 'Voyage legs')}
            <NotaDeAyuda texto={A.tramos} />
          </h3>
          <Tramos tramos={r.tramos} />
        </div>
      )}

      <div className="mt-4 rounded-md bg-papel px-3 py-2 text-sm">
        {r.exportacion ? (
          <div className="flex flex-wrap items-center gap-3">
            <span>
              {tr('Exportación:', 'Export:')}{' '}
              <Link
                to={`/seguimiento/${encodeURIComponent(r.exportacion.codigo)}`}
                className="font-medium text-verde hover:underline"
              >
                {r.exportacion.codigo}
              </Link>{' '}
              <span className="text-apagado">{textoOGuion(r.exportacion.exportador)}</span>
              <NotaDeAyuda texto={A.exportacion} />
            </span>
            <span>
              {tr('Contenedores completos:', 'Full containers:')}{' '}
              <span className={r.contenedoresCompletos < r.contenedores ? 'text-alerta' : 'text-verde-oscuro'}>
                {numero(r.contenedoresCompletos)} / {numero(r.contenedores)}
              </span>{' '}
              <NotaDeAyuda texto={A.completos} /> · {numero(r.bigBagsAsignados)} Big Bags
              <NotaDeAyuda texto={A.bigBagsAsignados} />
            </span>
            {permisos.editar && (
              <button
                type="button"
                className="text-xs text-apagado hover:text-alerta"
                disabled={vincular.isPending}
                onClick={() => {
                  if (
                    window.confirm(
                      tr(
                        '¿Desvincular la reserva de la exportación? Los contenedores ya asignados se quedan en la exportación.',
                        'Unlink the booking from the export? Containers already assigned stay with the export.',
                      ),
                    )
                  )
                    void ejecutar(async () => {
                      await vincular.mutateAsync({id: r.id, exportacion: null});
                      return null;
                    });
                }}
              >
                {tr('Desvincular', 'Unlink')}
              </button>
            )}
          </div>
        ) : permisos.editar ? (
          <VincularExportacion id={r.id} codigoInicial={codigoInicial} onMensaje={setMensaje} />
        ) : (
          <span className="text-apagado">{tr('Sin exportación vinculada.', 'No export linked.')}</span>
        )}
      </div>

      <div className="mt-4">
        <h3 className="text-sm font-medium">
          {tr('Historial de cambios', 'Change history')}
          <NotaDeAyuda texto={A.historial} />
        </h3>
        {cambios.length === 0 ? (
          <p className="mt-1 text-xs text-apagado">
            {tr(
              'Las fechas no han cambiado desde que se registró.',
              'The dates have not changed since it was registered.',
            )}
          </p>
        ) : (
          <ul className="mt-1 space-y-1 text-sm">
            {cambios.map(c => (
              <li
                key={c.id}
                className={`flex flex-wrap items-center justify-between gap-2 rounded-md border px-3 py-1.5 ${c.revisadoEl ? 'border-linea' : 'border-dorado/60 bg-dorado-claro/40'}`}
              >
                <span>
                  <span className="text-apagado">{c.campoEtiqueta}:</span>{' '}
                  <span className="line-through decoration-alerta/60">{valorLegible(c.antes)}</span> →{' '}
                  <span className="font-medium">{valorLegible(c.despues)}</span>
                </span>
                <span className="flex items-center gap-3 text-xs text-apagado">
                  <span>
                    {c.fuenteEtiqueta} · {fechaHora(c.detectadoEl)}
                  </span>
                  {c.revisadoEl ? (
                    <span>
                      {tr('Revisado', 'Reviewed')}
                      {c.revisadoPor ? ` (${c.revisadoPor})` : ''}
                    </span>
                  ) : (
                    permisos.editar && (
                      <button
                        type="button"
                        className="rounded-md border border-linea bg-white px-2 py-1 hover:bg-papel"
                        onClick={() => void ejecutar(async () => (await marcar.mutateAsync(c.id), null))}
                      >
                        {tr('✓ Marcar revisado', '✓ Mark as reviewed')}
                      </button>
                    )
                  )}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function Dato({titulo, valor, ayuda}: {titulo: string; valor: string; ayuda: Texto}) {
  return (
    <div>
      <dt className="text-xs text-apagado">
        {titulo}
        <NotaDeAyuda texto={ayuda} />
      </dt>
      <dd>{valor}</dd>
    </div>
  );
}
